/**
 * Único lugar donde se decide en qué configuración regional se escriben números
 * y fechas.
 *
 * Antes había trece `toLocaleString('es-MX')` repartidos por las pantallas. Con
 * el selector de idioma eso deja de ser una constante y pasa a ser un dato del
 * contexto: una interfaz en inglés con las cifras en formato mexicano es peor
 * que cualquiera de las dos cosas por separado.
 *
 * Los formateadores se memorizan porque construir un `Intl.NumberFormat` es
 * caro y las tablas del portal lo llamarían una vez por celda.
 */

/** El mismo guion que ya usaban las pantallas para "no hay dato". */
export const AUSENTE = '—';

const cacheNumero = new Map<string, Intl.NumberFormat>();
const cacheFecha = new Map<string, Intl.DateTimeFormat>();

export function formatoNumero(
  locale: string,
  valor: number,
  opciones?: Intl.NumberFormatOptions,
): string {
  const clave = locale + JSON.stringify(opciones ?? {});
  let formateador = cacheNumero.get(clave);
  if (!formateador) {
    formateador = new Intl.NumberFormat(locale, opciones);
    cacheNumero.set(clave, formateador);
  }
  return formateador.format(valor);
}

export function formatoFecha(
  locale: string,
  iso: string | null | undefined,
  opciones: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  if (!iso) return AUSENTE;
  const fecha = new Date(iso);
  // Una fecha inválida imprimiría "Invalid Date" en la pantalla de un cliente.
  if (Number.isNaN(fecha.getTime())) return AUSENTE;

  const clave = locale + JSON.stringify(opciones);
  let formateador = cacheFecha.get(clave);
  if (!formateador) {
    formateador = new Intl.DateTimeFormat(locale, opciones);
    cacheFecha.set(clave, formateador);
  }
  return formateador.format(fecha);
}

export function formatoFechaHora(locale: string, iso: string | null | undefined): string {
  return formatoFecha(locale, iso, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Una fecha de calendario, sin hora, **anclada a UTC**.
 *
 * Es para valores que son un día y no un instante: la fecha de un corte, el día
 * de un evento, la vigencia de un documento. `new Date('2026-08-21')` se parsea
 * como medianoche **UTC**, así que formatearla en la zona de quien mira la
 * retrocede un día en todo el continente americano — y el síntoma es un número
 * que no cuadra con el que está escrito en el código, sin ningún error de por
 * medio.
 *
 * **Ya mordió**, y por eso existe: el panel de inversionistas declaraba el corte
 * del `2026-08-21` y la pantalla decía «20 ago 2026». Lo atrapó la prueba que
 * comprueba que toda cifra del panel esté declarada, no una revisión.
 *
 * Es el mismo razonamiento de `formatoFechaHoraFija`, que ya estaba escrito para
 * las marcas de tiempo inventadas. Lo que faltaba era la variante sin hora.
 *
 * **Para un instante real no se usa esto**: ahí la zona de quien mira es la
 * correcta, y es lo que hace `formatoFechaHora`.
 */
export function formatoFechaDeCalendario(locale: string, iso: string | null | undefined): string {
  return formatoFecha(locale, iso, { dateStyle: 'medium', timeZone: 'UTC' });
}

/**
 * Igual que `formatoFechaHora`, pero anclado a UTC.
 *
 * Es para fechas **inventadas**, no para las de un usuario. Una marca de tiempo
 * de demostración convertida a la zona de quien mira se descoloca: un registro
 * que se escribió a las 9:42 de la mañana se enseña como las 3:42 de la
 * madrugada al otro lado del continente, y nadie se registra en una expo a esa
 * hora. Fijándola a UTC, la demo se lee igual desde donde sea — el mismo motivo
 * por el que la simulación lleva semilla fija.
 *
 * Para fechas reales **no se usa esto**: ahí la zona del usuario es la correcta.
 */
export function formatoFechaHoraFija(locale: string, iso: string | null | undefined): string {
  return formatoFecha(locale, iso, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  });
}
